import apiClient from './client';

const requireShape = (condition, label) => {
  if (!condition) throw new Error(`Unexpected ${label} response. Check the deployed API version.`);
};
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const amount = (value) => typeof value === 'number' && Number.isFinite(value);
const nullableString = (value) => value == null || typeof value === 'string';
const reportMetadata = (value) => ['source', 'vehicle_name', 'vin'].every((key) => nullableString(value[key]));
const totals = (value) => object(value) && ['total_earnings', 'total_cost', 'net'].every((key) => amount(value[key]));
const line = (value) => object(value) && typeof value.description === 'string' && amount(value.amount) &&
  ['earning', 'cost'].includes(value.type) && nullableString(value.category) && nullableString(value.occurred_on) &&
  (value.source_row == null || Number.isInteger(value.source_row));
const trend = (value) => Array.isArray(value) && value.every((item) => object(item) && typeof item.period === 'string' && totals(item));

export const parseOverview = (value) => {
  requireShape(object(value) && Array.isArray(value.periods) && totals(value.totals) &&
    Number.isInteger(value.totals.vehicle_count) && trend(value.trend) &&
    (value.investor == null || object(value.investor)) &&
    Array.isArray(value.vehicles) && value.vehicles.every((car) => object(car) && typeof car.vehicle_id === 'string' &&
      nullableString(car.thumbnail_url) && nullableString(car.make) && nullableString(car.model) && reportMetadata(car) && totals(car)),
  'owner overview');
  return value;
};
export const parseSummary = (value) => {
  requireShape(object(value) && totals(value.totals) && trend(value.trend), 'owner summary');
  return value;
};
export const parseLineItems = (value) => {
  requireShape(object(value) && Array.isArray(value.periods) && Number.isInteger(value.count) && totals(value.totals) &&
    Array.isArray(value.line_items) && value.line_items.every((item) => line(item) && reportMetadata(item) && typeof item.vehicle_id === 'string' && typeof item.period === 'string'),
  'owner line items');
  requireShape(value.count === value.line_items.length, 'complete owner line items');
  return value;
};
export const parseDetail = (value) => {
  requireShape(object(value) && totals(value.totals) && Array.isArray(value.statements) &&
    (value.vehicle == null || (object(value.vehicle) && reportMetadata(value.vehicle))) && nullableString(value.thumbnail_url) &&
    value.statements.every((item) => totals(item) && reportMetadata(item) && Array.isArray(item.earnings) && item.earnings.every(line) && Array.isArray(item.costs) && item.costs.every(line)),
  'vehicle statements');
  return value;
};
export const parseImport = (value) => {
  requireShape(object(value) && object(value.import) && typeof value.import.status === 'string' &&
    Array.isArray(value.import.errors) && value.import.errors.every((error) => object(error) && typeof error.reason === 'string' &&
      (error.row == null || Number.isInteger(error.row))) &&
    nullableString(value.import.error_message) && Number.isInteger(value.import.statements_written), 'finance import');
  if (value.statements !== undefined) requireShape(Array.isArray(value.statements) && value.statements.every(totals), 'preview statements');
  return value;
};
export const parseInvestorReport = (value) => {
  parseImport(value);
  requireShape(value.import.mode === 'investor_report' && typeof value.import.investor_id === 'string' &&
    typeof value.import.period === 'string' && Array.isArray(value.import.periods) &&
    value.import.periods.every((period) => typeof period === 'string') && typeof value.import.dry_run === 'boolean' &&
    Array.isArray(value.statements) && value.statements.every((statement) =>
      typeof statement.id === 'string' && typeof statement.period === 'string' &&
      typeof statement.vehicle_id === 'string' && typeof statement.investor_id === 'string' &&
      statement.source === 'investor_report' && reportMetadata(statement) &&
      Array.isArray(statement.earnings) && statement.earnings.every(line) &&
      Array.isArray(statement.costs) && statement.costs.every(line)),
  'investor report import');
  requireShape(value.warnings === undefined || (Array.isArray(value.warnings) && value.warnings.every((warning) => typeof warning === 'string')), 'investor report warnings');
  return { ...value, warnings: value.warnings || [] };
};
export const parseList = (value, key) => {
  requireShape(object(value) && Array.isArray(value[key]), key);
  return value[key];
};
export const parseInvestor = (value) => {
  requireShape(object(value) && object(value.investor) && typeof value.investor.id === 'string' &&
    typeof value.investor.name === 'string' && (value.investor.username === undefined || nullableString(value.investor.username)) &&
    nullableString(value.investor.email) && nullableString(value.investor.phone) && nullableString(value.investor.notes), 'investor');
  return value.investor;
};

const ownerProfile = (value) => object(value) && typeof value.id === 'string' && nullableString(value.plate_number) &&
  nullableString(value.Make) && nullableString(value.Model) && (value.year == null || Number.isInteger(value.year)) &&
  nullableString(value.description) && nullableString(value.vin) && nullableString(value.vehicle_name) && nullableString(value.Owner_investor_id);
export const parseOwnerVehicles = (value, investorId) => {
  requireShape(object(value) && object(value.investor) && typeof value.investor.id === 'string' && typeof value.period === 'string' &&
    Number.isInteger(value.count) && Array.isArray(value.vehicles) && value.count === value.vehicles.length &&
    value.vehicles.every((car) => ownerProfile(car) && nullableString(car.thumbnail_url) && totals(car.totals) && typeof car.has_statement === 'boolean'),
  'owned vehicles');
  requireShape(!investorId || (value.investor.id === investorId && value.vehicles.every((car) => car.Owner_investor_id === investorId)), 'owned vehicles scope');
  return value;
};
export const parseOwnerVehicle = (value) => {
  requireShape(object(value) && ownerProfile(value.profile) && nullableString(value.thumbnail_url), 'owned vehicle profile');
  return value;
};

export const ownerApi = {
  vehicles: async (filters, signal) => parseOwnerVehicles(await apiClient.getOwnerVehicles(filters, { signal }), filters.investor_id),
  vehicle: async (id, filters, signal) => parseOwnerVehicle(await apiClient.getOwnerVehicle(id, filters, { signal })),
  overview: async (filters, signal) => parseOverview(await apiClient.getOwnerOverview(filters, { signal })),
  summary: async (filters, signal) => parseSummary(await apiClient.getOwnerSummary(filters, { signal })),
  lines: async (filters, signal) => parseLineItems(await apiClient.getOwnerStatements(filters, { signal })),
  detail: async (id, filters, signal) => parseDetail(await apiClient.getOwnerVehicleStatements(id, filters, { signal })),
};
export const investorApi = {
  list: async (status, signal) => parseList(await apiClient.getInvestors(status, { signal }), 'investors').map((investor) => parseInvestor({ investor })),
  get: async (id, signal) => parseInvestor(await apiClient.getInvestor(id, { signal })),
  vehicles: async (id, signal) => {
    const ids = parseList(await apiClient.getInvestorVehicles(id, { signal }), 'vehicle_ids');
    requireShape(ids.every((value) => typeof value === 'string'), 'investor vehicle IDs');
    return ids;
  },
  create: async (payload) => parseInvestor(await apiClient.createInvestor(payload)),
  update: async (id, payload) => parseInvestor(await apiClient.updateInvestor(id, payload)),
};
export const ownershipApi = {
  assign: async (id, payload) => {
    const response = await apiClient.assignVehicleOwner(id, payload);
    requireShape(profileIsValid(response?.profile) && response.profile.id === id &&
      response.profile.Owner_investor_id === payload.investor_id, 'assigned owner');
    return response.profile;
  },
  release: async (id) => {
    const response = await apiClient.releaseVehicleOwner(id);
    requireShape(profileIsValid(response?.profile) && response.profile.id === id &&
      response.profile.Owner_investor_id === null, 'unassigned owner');
    return response.profile;
  },
};
const profileFrom = (item) => item?.profile || item;
const profileIsValid = (profile) => object(profile) && typeof profile.id === 'string' &&
  nullableString(profile.plate_number) && nullableString(profile.Make) && nullableString(profile.Model) &&
  (profile.year == null || Number.isInteger(profile.year)) && nullableString(profile.description) &&
  nullableString(profile.Owner_investor_id);
const stringArray = (value) => Array.isArray(value) && value.every((item) => typeof item === 'string');
export const parseMerge = (response, request) => {
  requireShape(object(response) && typeof response.dry_run === 'boolean' && profileIsValid(response.target) &&
    stringArray(response.source_ids) && stringArray(response.moved_cost_ids) && stringArray(response.warnings) &&
    (response.moved_reservation_ids === undefined || stringArray(response.moved_reservation_ids)) &&
    (response.legacy_reservation_ids === undefined || stringArray(response.legacy_reservation_ids)) &&
    stringArray(response.target.Picture_links) && response.target.Picture_links.length <= 4 &&
    (response.target.Owner_investor_id === null || typeof response.target.Owner_investor_id === 'string'), 'profile merge');
  requireShape(response.dry_run === request.dry_run && response.target.id === request.target_id &&
    response.source_ids.length === request.source_ids.length &&
    new Set(response.source_ids).size === response.source_ids.length &&
    response.source_ids.every((id) => request.source_ids.includes(id)) &&
    (!request.investor_id || response.target.Owner_investor_id === request.investor_id), 'profile merge scope');
  return response;
};
export const vehicleApi = {
  merge: async (payload) => parseMerge(await apiClient.mergeVehicleProfiles(payload), payload),
  list: async (filters = {}, signal) => {
    const response = await apiClient.listVehicleProfiles(filters, { signal });
    requireShape(object(response) && Array.isArray(response.vehicle_profiles) && Number.isInteger(response.count) &&
      Number.isInteger(response.offset) && Number.isInteger(response.limit) &&
      response.vehicle_profiles.every((item) => profileIsValid(profileFrom(item))), 'vehicle profiles');
    return { ...response, vehicle_profiles: response.vehicle_profiles.map((item) => ({
      profile: profileFrom(item), pictures: Array.isArray(item.pictures) ? item.pictures : [],
    })) };
  },
  get: async (id, signal) => {
    const response = await apiClient.getVehicleProfile(id, { signal });
    requireShape(object(response) && profileIsValid(response.profile) &&
      (response.pictures === undefined || Array.isArray(response.pictures)), 'vehicle profile');
    return { ...response, pictures: response.pictures || [] };
  },
  create: async (payload) => {
    const response = await apiClient.createVehicleProfile(payload);
    requireShape(object(response) && profileIsValid(response.profile), 'created vehicle profile');
    return response.profile;
  },
  update: async (id, payload) => {
    const response = await apiClient.patchVehicleProfile(id, payload);
    requireShape(object(response) && profileIsValid(response.profile), 'updated vehicle profile');
    return response.profile;
  },
};
const validCost = (cost) => object(cost) && typeof cost.id === 'string' && typeof cost.vehicle_id === 'string' &&
  typeof cost.date === 'string' && amount(cost.amount) && typeof cost.title === 'string' &&
  typeof cost.description === 'string' && typeof cost.etag === 'string';
export const costApi = {
  list: async (id, signal) => {
    const response = await apiClient.getVehicleCosts(id, { signal });
    requireShape(object(response) && Array.isArray(response.costs) && Number.isInteger(response.count) &&
      response.costs.every(validCost), 'vehicle costs');
    return response.costs;
  },
  create: async (id, payload) => {
    const response = await apiClient.createVehicleCost(id, payload);
    requireShape(object(response) && validCost(response.cost), 'created vehicle cost');
    return response.cost;
  },
  update: async (id, costId, payload, etag) => {
    const response = await apiClient.updateVehicleCost(id, costId, payload, etag);
    requireShape(object(response) && validCost(response.cost), 'updated vehicle cost');
    return response.cost;
  },
  remove: (id, costId, etag) => apiClient.deleteVehicleCost(id, costId, etag),
};
export const financeApi = {
  list: async (investorId, signal) => parseList(await apiClient.getFinanceImports({ investor_id: investorId }, { signal }), 'imports').map((audit) => {
    const parsed = parseImport({ import: audit }).import;
    requireShape(parsed.investor_id === investorId, 'investor-filtered import history');
    return parsed;
  }),
  get: async (id, period, signal) => parseImport(await apiClient.getFinanceImport(id, period, { signal })).import,
  upload: async (options) => parseInvestorReport(await apiClient.importInvestorReport(options)),
};
